/**
 * Inventory List Component
 * Handles the table view of equipment
 */

const InventoryList = {
    currentCategoryFilter: 'all',
    currentCourseFilter: 'all',
    currentStatusFilter: 'all',
    currentPage: 1,
    itemsPerPage: 20,
    searchQuery: '',

    STATUS: {
        available: ['Disponible', 'is-available'],
        in_use: ['En uso', 'is-in-use'],
        maintenance: ['Mantenimiento', 'is-maintenance'],
        broken: ['Roto', 'is-broken']
    },

    render() {
        const items = InventoryService.getAllItems();
        const categories = InventoryService.categoriesInUse(items);
        const courses = InventoryService.getCourses();
        const esc = v => Utils.sanitizeHTML(String(v ?? ''));

        // Student restrictions: only items for THEIR courses
        const isStudent = AuthManager.isStudent() && !AuthManager.canManageWarehouse();
        let studentAllowedKeys = []; // Will contain formation IDs AND academic years
        if (isStudent) {
            const user = AuthManager.getCurrentUser();
            const students = StudentService.getStudents();
            const student = students.find(s => s.id === user.studentId || s.email === user.username);
            if (student) {
                const sFormations = student.formations || [];
                studentAllowedKeys = [...sFormations];
                // Also add academic years of those formations
                sFormations.forEach(fId => {
                    const formation = FormationService.getFormation(fId);
                    if (formation && formation.academicYear) {
                        studentAllowedKeys.push(formation.academicYear.toString());
                    }
                });
            }
        }
        const visible = items.filter(i => !isStudent || (i.allowedCourses || []).some(key => studentAllowedKeys.includes(key)));

        // Apply filters & Search
        const q = (this.searchQuery || '').toLowerCase();
        const filteredItems = visible.filter(i => {
            const matchesCategory = this.currentCategoryFilter === 'all' || i.category === this.currentCategoryFilter;
            const matchesCourse = this.currentCourseFilter === 'all' || (i.allowedCourses && i.allowedCourses.includes(this.currentCourseFilter));
            const matchesStatus = this.currentStatusFilter === 'all' || i.status === this.currentStatusFilter;
            const matchesSearch = !q || String(i.name || '').toLowerCase().includes(q) || String(i.serialNumber || '').toLowerCase().includes(q);
            return matchesCategory && matchesCourse && matchesStatus && matchesSearch;
        });

        // Figures of everything this user can see (not just the current page).
        const units = visible.reduce((n, i) => n + (Number(i.totalQuantity) || 0), 0);
        const shelf = visible.reduce((n, i) => n + (Number(i.quantity) || 0), 0);
        const usedCategories = new Set(visible.map(i => i.category)).size;
        const review = visible.filter(i => ['maintenance', 'broken'].includes(i.status));
        const maint = review.filter(i => i.status === 'maintenance').length, broken = review.length - maint;
        const pct = units ? Math.round(shelf / units * 100) : 0;

        // Pagination Logic
        const totalItems = filteredItems.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / this.itemsPerPage));
        if (this.currentPage > totalPages) this.currentPage = totalPages;
        const startIndex = (this.currentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        const paginatedItems = filteredItems.slice(startIndex, endIndex);

        return `
            <div class="wh-figs">
                <div class="wh-fig"><span class="wh-fig-label">Elementos</span><b>${visible.length}</b><small>en ${usedCategories} ${usedCategories === 1 ? 'categoría' : 'categorías'}</small></div>
                <div class="wh-fig"><span class="wh-fig-label">Unidades en estantería</span><b class="is-ok">${shelf} <span>de ${units}</span></b><span class="wh-bar" role="img" aria-label="${pct} % en estantería"><span style="width: ${pct}%;"></span></span></div>
                <div class="wh-fig"><span class="wh-fig-label">Fuera de estantería</span><b>${Math.max(0, units - shelf)}</b><small>prestadas o en revisión</small></div>
                ${isStudent ? '' : `<div class="wh-fig"><span class="wh-fig-label">Revisar</span><b class="${review.length ? 'is-bad' : ''}">${review.length}</b><small>${review.length ? `${maint} en mantenimiento · ${broken} ${broken === 1 ? 'roto' : 'rotos'}` : 'Todo en orden'}</small></div>`}
            </div>

            <section class="wh-card">
                <div class="wh-toolbar">
                    <div class="search-box wh-search">
                        <i class="fas fa-search"></i>
                        <input type="text" id="inventory-search" placeholder="Buscar por nombre o número de serie" aria-label="Buscar en el inventario" value="${esc(this.searchQuery)}" ${UIEvents.attr("input", function(event,captured){InventoryList.handleSearch(this.value);}, [])}>
                    </div>
                    <select id="category-filter" aria-label="Categoría" ${UIEvents.attr("change", function(event,captured){InventoryList.handleFilter('category',this.value);}, [])} class="form-select wh-pill-select">
                        <option value="all">Todas las categorías</option>
                        ${categories.map(c => `<option value="${esc(c.id)}" ${this.currentCategoryFilter === c.id ? 'selected' : ''}>${esc(c.label)}</option>`).join('')}
                    </select>
                    <select id="status-filter" aria-label="Estado" ${UIEvents.attr("change", function(event,captured){InventoryList.handleFilter('status',this.value);}, [])} class="form-select wh-pill-select">
                        <option value="all">Todos los estados</option>
                        ${Object.entries(this.STATUS).map(([id, [label]]) => `<option value="${id}" ${this.currentStatusFilter === id ? 'selected' : ''}>${label}</option>`).join('')}
                    </select>
                    ${!isStudent ? `
                    <select id="course-filter" aria-label="Curso" ${UIEvents.attr("change", function(event,captured){InventoryList.handleFilter('course',this.value);}, [])} class="form-select wh-pill-select">
                        <option value="all">Todos los cursos</option>
                        ${courses.map(c => `<option value="${esc(c.id)}" ${this.currentCourseFilter === c.id ? 'selected' : ''}>${esc(c.label)}</option>`).join('')}
                    </select>` : ''}
                    ${AuthManager.canManageWarehouse() ? `<button type="button" class="btn btn-secondary whc-open" ${UIEvents.attr("click", function(event,captured){InventoryList.openCategories();}, [])}><i class="fas fa-cog" aria-hidden="true"></i> Categorías</button>` : ''}
                </div>

                <div class="wh-table-wrap">
                    <table class="wh-table">
                        <thead>
                            <tr>
                                <th scope="col">Elemento</th>
                                <th scope="col">Categoría</th>
                                <th scope="col" class="wh-col-stock">Disponibles</th>
                                <th scope="col">Estado</th>
                                <th scope="col">Pueden pedirlo</th>
                                ${AuthManager.canManageWarehouse() ? '<th scope="col"><span class="wh-sr">Acciones</span></th>' : ''}
                            </tr>
                        </thead>
                        <tbody>
                            ${paginatedItems.length ? paginatedItems.map(item => this.renderRow(item)).join('') : `<tr><td colspan="6"><div class="dnr-empty">${visible.length ? 'Ningún elemento coincide con los filtros.' : 'Aún no hay elementos en el inventario.'}</div></td></tr>`}
                        </tbody>
                    </table>
                </div>

                <div class="wh-pagination">
                    <div class="wh-pagination-info">${totalItems ? `${startIndex + 1}–${Math.min(endIndex, totalItems)} de ${totalItems} ${totalItems === 1 ? 'elemento' : 'elementos'}` : '0 elementos'}</div>
                    <select id="per-page-filter" aria-label="Elementos por página" ${UIEvents.attr("change", function(event,captured){InventoryList.handleItemsPerPage(this.value);}, [])} class="form-select wh-pill-select">
                        ${[10, 20, 50, 100].map(n => `<option value="${n}" ${this.itemsPerPage == n ? 'selected' : ''}>${n} por página</option>`).join('')}
                    </select>
                    <div class="wh-pagination-buttons">
                        <button type="button" class="wh-page-btn" aria-label="Página anterior" ${UIEvents.attr("click", function(event,captured){InventoryList.handlePage(-1);}, [])} ${this.currentPage === 1 ? 'disabled' : ''}><i class="fas fa-chevron-left"></i></button>
                        <span class="wh-pagination-page">${this.currentPage} / ${totalPages}</span>
                        <button type="button" class="wh-page-btn" aria-label="Página siguiente" ${UIEvents.attr("click", function(event,captured){InventoryList.handlePage(1);}, [])} ${this.currentPage >= totalPages ? 'disabled' : ''}><i class="fas fa-chevron-right"></i></button>
                    </div>
                </div>
            </section>
        `;
    },

    // «Dirección 1º, Foto 1º +2»: the first two courses and how many more.
    coursesSummary(item) {
        const names = (item.allowedCourses || []).map(c => {
            const formation = FormationService.getFormation(c);
            return formation ? formation.name : String(c);
        });
        if (!names.length) return '<span class="wh-muted">Ningún curso</span>';
        const shown = names.slice(0, 2).map(n => Utils.sanitizeHTML(n)).join(', ');
        const more = names.length - 2;
        return `<span title="${Utils.sanitizeHTML(names.join(', '))}">${shown}${more > 0 ? ` <b class="wh-more">+${more}</b>` : ''}</span>`;
    },

    renderRow(item) {
        const safe = (val) => Utils.sanitizeHTML(val ?? '');
        const categoryLabel = InventoryService.categoryLabel(item.category);
        const [statusLabel, statusCls] = this.STATUS[item.status] || [item.status, ''];
        const available = Number(item.quantity) || 0, total = Number(item.totalQuantity) || 0;
        const pct = total ? Math.round(available / total * 100) : 0;
        const barCls = available === 0 ? 'is-empty' : pct < 40 ? 'is-low' : '';
        return `
            <tr>
                <td>
                    <div class="wh-item">
                        <button type="button" class="wh-item-thumb" aria-label="Ver la foto de ${safe(item.name)}" ${UIEvents.attr("click", function(event,captured){InventoryList.showImage(String(captured[0]),String(captured[1]));}, [item.photo || '', item.name || ''])}>
                            ${item.photo ? `<img src="${safe(item.photo)}" alt="">` : '<i class="fas fa-image"></i>'}
                        </button>
                        <span><span class="wh-item-name">${safe(item.name)}</span>${item.serialNumber ? `<span class="wh-item-serial">${safe(item.serialNumber)}</span>` : ''}</span>
                    </div>
                </td>
                <td><span class="wh-category-tag">${safe(categoryLabel)}</span></td>
                <td>
                    <div class="wh-stock">
                        <span class="wh-bar ${barCls}"><span style="width: ${pct}%;"></span></span>
                        <b>${available} de ${total}</b>
                    </div>
                </td>
                <td><span class="wh-status-badge ${statusCls}">${safe(statusLabel)}</span></td>
                <td class="wh-courses">${this.coursesSummary(item)}</td>
                ${AuthManager.canManageWarehouse() ? `
                <td>
                    <div class="wh-row-actions">
                        <button type="button" class="btn-icon" ${UIEvents.attr("click", function(event,captured){InventoryList.editItem(String(captured[0]));}, [item.id])} title="Editar" aria-label="Editar ${safe(item.name)}"><i class="fas fa-pen"></i></button>
                        <button type="button" class="btn-icon is-danger" ${UIEvents.attr("click", function(event,captured){InventoryList.deleteItem(String(captured[0]));}, [item.id])} title="Eliminar" aria-label="Eliminar ${safe(item.name)}"><i class="fas fa-trash-alt"></i></button>
                    </div>
                </td>` : ''}
            </tr>
        `;
    },

    init() {
        console.log('InventoryList init');
    },

    attachEvents() {
        // Event listeners can be attached here if needed
    },

    handleFilter(type, value) {
        if (type === 'category') {
            this.currentCategoryFilter = value;
        } else if (type === 'course') {
            this.currentCourseFilter = value;
        } else if (type === 'status') {
            this.currentStatusFilter = value;
        }
        this.currentPage = 1; // Reset to first page on filter change
        this.updateView();
    },

    handleItemsPerPage(value) {
        this.itemsPerPage = parseInt(value);
        this.currentPage = 1;
        this.updateView();
    },

    handlePage(delta) {
        const newPage = this.currentPage + delta;
        if (newPage > 0) {
            this.currentPage = newPage;
            this.updateView();
        }
    },

    handleSearch(query) {
        this.searchQuery = query;
        this.currentPage = 1;
        this.updateView();

        // Restore focus and cursor position
        const input = document.getElementById('inventory-search');
        if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
        }
    },

    updateView() {
        const container = document.getElementById('inventory-list-container');
        if (container) {
            SafeHTML.set(container, this.render());
        }
    },

    // ── Categorías (diseño A, 2026-10-29): la lista del centro con cuántos equipos tiene cada una,
    // editar nombre e icono, ocultar o volver a mostrar. «Otros» siempre está visible.
    openCategories() {
        this._catEditing = null;
        this._catConfirm = null;
        ModalManager.show('Categorías del almacén', '<div id="whc-body" class="whc dnr-proposal"></div>', [
            { text: 'Cerrar', class: 'btn-secondary', action: () => ModalManager.close() }
        ], () => InventoryList.updateView());
        this.paintCategories();
    },

    paintCategories() {
        const box = document.getElementById('whc-body');
        if (!box) return;
        const esc = v => Utils.sanitizeHTML(String(v ?? ''));
        const items = InventoryService.getAllItems();
        const count = id => items.filter(i => i.category === id).length;
        const all = InventoryService.getCategories(true);
        const editing = this._catEditing;
        const form = cat => `
            <form class="whc-form" id="whc-form" ${UIEvents.attr("submit", function(event,captured){event.preventDefault();InventoryList.saveCategory();}, [])}>
                <label class="whc-label" for="whc-name">Nombre</label>
                <input id="whc-name" class="form-input" maxlength="40" required value="${esc(cat?.label || '')}" placeholder="Ej.: Vestuario">
                <span class="whc-label">Icono</span>
                <div class="whc-icons" role="radiogroup" aria-label="Icono">
                    ${InventoryService.CATEGORY_ICONS.map(icon => `<label class="whc-icon" title="${esc(icon)}"><input type="radio" name="whc-icon" value="${esc(icon)}" ${(cat?.icon || 'fa-box') === icon ? 'checked' : ''}><i class="fas ${esc(icon)}" aria-hidden="true"></i></label>`).join('')}
                </div>
                <div class="whc-form-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){InventoryList._catEditing=null;InventoryList.paintCategories();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-primary">${cat ? 'Guardar' : 'Crear categoría'}</button>
                </div>
            </form>`;
        const row = cat => editing === cat.id ? `<li class="whc-row is-editing">${form(cat)}</li>` : `
            <li class="whc-row${cat.active ? '' : ' is-hidden'}">
                <span class="whc-ico"><i class="fas ${esc(cat.icon)}" aria-hidden="true"></i></span>
                <span class="whc-name">${esc(cat.label)}</span>
                <span class="whc-count">${count(cat.id)} ${count(cat.id) === 1 ? 'equipo' : 'equipos'}</span>
                <span class="whc-actions">
                    ${cat.active ? `<button type="button" class="whc-btn" aria-label="Editar ${esc(cat.label)}" ${UIEvents.attr("click", function(event,captured){InventoryList._catEditing=String(captured[0]);InventoryList.paintCategories();}, [cat.id])}><i class="fas fa-pen" aria-hidden="true"></i></button>` : ''}
                    ${cat.id === 'other' ? '<span class="whc-fixed">fija</span>'
                        : `<button type="button" class="whc-btn whc-toggle" ${UIEvents.attr("click", function(event,captured){InventoryList.toggleCategory(String(captured[0]));}, [cat.id])}>${cat.active ? 'Ocultar' : 'Mostrar'}</button>`}
                    ${cat.id !== 'other' && !count(cat.id) ? `<button type="button" class="whc-btn whc-delete" aria-label="Eliminar ${esc(cat.label)}" ${UIEvents.attr("click", function(event,captured){InventoryList._catConfirm=String(captured[0]);InventoryList.paintCategories();}, [cat.id])}><i class="fas fa-trash" aria-hidden="true"></i></button>` : ''}
                </span>
                ${this._catConfirm === cat.id ? `<div class="whc-confirm" role="alert">¿Eliminar «${esc(cat.label)}»? No tiene equipos.
                    <button type="button" class="whc-btn" ${UIEvents.attr("click", function(event,captured){InventoryList._catConfirm=null;InventoryList.paintCategories();}, [])}>No</button>
                    <button type="button" class="whc-btn whc-delete-yes" ${UIEvents.attr("click", function(event,captured){InventoryList.deleteCategory(String(captured[0]));}, [cat.id])}>Sí, eliminar</button></div>` : ''}
            </li>`;
        const visible = all.filter(c => c.active), hidden = all.filter(c => !c.active);
        SafeHTML.set(box, `
            <p class="whc-hint">El nombre y el icono se pueden cambiar sin mover ningún equipo. Una categoría oculta deja de salir al dar de alta material, pero sus equipos la conservan. Solo se elimina una categoría sin equipos.</p>
            <ul class="whc-list">${visible.map(row).join('')}</ul>
            ${hidden.length ? `<h3 class="whc-sub">Ocultas</h3><ul class="whc-list">${hidden.map(row).join('')}</ul>` : ''}
            ${editing === '__new' ? form(null) : `<button type="button" class="btn btn-primary whc-new" ${UIEvents.attr("click", function(event,captured){InventoryList._catEditing='__new';InventoryList.paintCategories();}, [])}><i class="fas fa-plus" aria-hidden="true"></i> Nueva categoría</button>`}`);
        document.getElementById('whc-name')?.focus();
    },

    async saveCategory() {
        const name = document.getElementById('whc-name')?.value.trim();
        const icon = document.querySelector('input[name="whc-icon"]:checked')?.value || 'fa-box';
        if (!name) return;
        const editing = this._catEditing;
        const saved = editing === '__new' ? await InventoryService.addCategory({ name, icon }) : await InventoryService.updateCategory(editing, { name, icon });
        if (!saved) return;
        Utils.showToast(editing === '__new' ? 'Categoría creada' : 'Categoría guardada');
        this._catEditing = null;
        this.paintCategories();
    },

    async deleteCategory(id) {
        this._catConfirm = null;
        if (await InventoryService.deleteCategory(id)) Utils.showToast('Categoría eliminada');
        this.paintCategories();
    },

    async toggleCategory(id) {
        const cat = InventoryService.getCategories(true).find(c => c.id === id);
        if (!cat || !await InventoryService.updateCategory(id, { active: !cat.active })) return;
        this.paintCategories();
    },

    openAddModal() {
        // Use ModalManager to show add form
        const content = `
            <div class="dnr-proposal wh-form-dnr">
                <form id="add-item-form">
                    <div class="form-group">
                        <label class="form-label">Nombre del Elemento</label>
                        <input type="text" name="itemName" class="form-input" required>
                    </div>
                    <div class="wh-form-row-2">
                        <div class="form-group">
                            <label class="form-label">Categoría</label>
                            <select name="category" class="form-select">
                                ${InventoryService.getCategories().map(c => `<option value="${Utils.sanitizeHTML(c.id)}">${Utils.sanitizeHTML(c.label)}</option>`).join('')}
                            </select>
                        </div>
                        <div class="form-group">
                            <label class="form-label">Cantidad</label>
                            <input type="number" name="quantity" class="form-input" value="1" min="1" required>
                        </div>
                    </div>
                    <!-- ID/Serial -->
                    <div class="form-group">
                        <label class="form-label">Número de Serie / ID</label>
                        <input type="text" name="serialNumber" class="form-input" placeholder="Opcional">
                    </div>

                    <div class="form-group">
                        <label class="form-label">Foto del Equipo</label>
                        <input type="file" id="item-photo" name="photo" class="form-input" accept="image/*">
                    </div>

                    <div class="form-group">
                        <label class="form-label">Cursos Permitidos</label>
                        <div class="wh-courses-check-grid">
                            ${InventoryService.getCourses().map(c => `
                                <label class="wh-check-row">
                                    <input type="checkbox" name="allowedCourses" value="${Utils.sanitizeHTML(c.id)}"> ${Utils.sanitizeHTML(c.label)}
                                </label>
                            `).join('')}
                        </div>
                    </div>
                </form>
            </div>
        `;

        ModalManager.show('Añadir Nuevo Elemento', content, [
            {
                text: 'Cancelar',
                class: 'btn-secondary',
                action: () => ModalManager.close()
            },
            {
                text: 'Guardar',
                class: 'btn-primary',
                action: async () => {
                    if (InventoryList._savingItem) return;
                    InventoryList._savingItem = true;
                    try {
                    const form = document.getElementById('add-item-form');
                    if (!form.checkValidity()) {
                        form.reportValidity();
                        return;
                    }

                    // Handle Photo Upload
                    const photoInput = document.getElementById('item-photo');
                    let photoPath = '';

                    if (photoInput.files.length > 0) {
                        const file = photoInput.files[0];
                        Utils.showToast('Subiendo imagen...', 'info');
                        photoPath = await InventoryService.uploadPhoto(file);
                        if (!photoPath) {
                            Utils.showToast('Error al subir imagen', 'error');
                            return;
                        }
                    }

                    const formData = new FormData(form);
                    const allowed = [];
                    form.querySelectorAll('input[name="allowedCourses"]:checked').forEach(cb => allowed.push(cb.value));

                    const data = {
                        name: formData.get('itemName'),
                        category: formData.get('category'),
                        quantity: formData.get('quantity'),
                        serialNumber: formData.get('serialNumber'),
                        allowedCourses: allowed,
                        status: 'available',
                        photo: photoPath
                    };

                    if (!await InventoryService.addItem(data) || !form.isConnected) return;
                    Utils.showToast('Elemento añadido correctamente');
                    ModalManager.close();
                    InventoryList.handleFilter(InventoryList.currentCategoryFilter); // Refresh
                    } finally { InventoryList._savingItem = false; }
                }
            }
        ]);
    },

    editItem(id) {
        const item = InventoryService.getItemById(id);
        if (!item) return;

        // Populate and show edit modal
        const content = `
            <div class="dnr-proposal wh-form-dnr">
                <form id="edit-item-form">
                    <input type="hidden" name="id" value="${Utils.sanitizeHTML(item.id)}">

                    <div class="wh-form-row-photo">
                        <div class="form-group wh-photo-box">
                            <label class="form-label">Foto Actual</label>
                            <div class="wh-photo-preview">
                                ${item.photo ? `<img src="${Utils.sanitizeHTML(item.photo)}">` : '<i class="fas fa-image"></i>'}
                            </div>
                        </div>
                        <div class="form-group" style="flex:1;">
                            <label class="form-label">Cambiar Foto</label>
                            <input type="file" id="item-photo-edit" name="photo" class="form-input" accept="image/*">
                            <small class="text-muted">Deja vacío para mantener la actual</small>
                        </div>
                    </div>

                    <div class="form-group">
                        <label class="form-label">Nombre del Elemento</label>
                        <input type="text" name="itemName" class="form-input" value="${Utils.sanitizeHTML(item.name)}" required>
                    </div>

                    <div class="wh-form-row-2">
                        <div class="form-group">
                            <label class="form-label">Categoría</label>
                            <select name="category" class="form-select">
                                ${InventoryService.getCategories(true).filter(c => c.active || c.id === item.category).map(c => `<option value="${Utils.sanitizeHTML(c.id)}" ${c.id === item.category ? 'selected' : ''}>${Utils.sanitizeHTML(c.label)}</option>`).join('')}
                            </select>
                        </div>
                        <div class="form-group">
                            <label class="form-label">Cantidad Total</label>
                            <input type="number" name="totalQuantity" class="form-input" value="${item.totalQuantity || item.quantity}" min="0" required>
                            <small class="text-muted">Disponibles actualmente: <strong>${item.quantity}</strong></small>
                        </div>
                    </div>

                    <div class="wh-form-row-2">
                        <div class="form-group">
                            <label class="form-label">Número de Serie / ID</label>
                            <input type="text" name="serialNumber" class="form-input" value="${Utils.sanitizeHTML(item.serialNumber || '')}">
                        </div>
                        <div class="form-group">
                            <label class="form-label">Estado</label>
                            <select name="status" class="form-select">
                                <option value="available" ${item.status === 'available' ? 'selected' : ''}>Disponible</option>
                                <option value="in_use" ${item.status === 'in_use' ? 'selected' : ''}>En Uso</option>
                                <option value="maintenance" ${item.status === 'maintenance' ? 'selected' : ''}>Mantenimiento</option>
                                <option value="broken" ${item.status === 'broken' ? 'selected' : ''}>Roto</option>
                            </select>
                        </div>
                    </div>

                    <div class="form-group">
                        <label class="form-label">Cursos Permitidos</label>
                        <div class="wh-courses-check-grid">
                            ${InventoryService.getCourses().map(c => `
                                <label class="wh-check-row">
                                    <input type="checkbox" name="allowedCourses" value="${Utils.sanitizeHTML(c.id)}" ${(item.allowedCourses || []).includes(c.id) ? 'checked' : ''}> ${Utils.sanitizeHTML(c.label)}
                                </label>
                            `).join('')}
                        </div>
                    </div>
                </form>
            </div>
        `;

        ModalManager.show('Editar Elemento', content, [
            {
                text: 'Cancelar',
                class: 'btn-secondary',
                action: () => ModalManager.close()
            },
            {
                text: 'Guardar Cambios',
                class: 'btn-primary',
                action: async () => {
                    if (InventoryList._savingItem) return;
                    InventoryList._savingItem = true;
                    try {
                    const form = document.getElementById('edit-item-form');
                    if (!form.checkValidity()) {
                        form.reportValidity();
                        return;
                    }

                    // Handle Photo Upload
                    const photoInput = document.getElementById('item-photo-edit');
                    let photoPath = item.photo; // Default to existing

                    if (photoInput.files.length > 0) {
                        const file = photoInput.files[0];
                        Utils.showToast('Subiendo nueva imagen...', 'info');
                        const uploaded = await InventoryService.uploadPhoto(file);
                        if (uploaded) {
                            photoPath = uploaded;
                        } else {
                            Utils.showToast('Error al subir imagen', 'error');
                            return;
                        }
                    }

                    const formData = new FormData(form);
                    const allowed = [];
                    form.querySelectorAll('input[name="allowedCourses"]:checked').forEach(cb => allowed.push(cb.value));

                    // Availability is calculated by the server from outstanding loans.
                    const newTotal = parseInt(formData.get('totalQuantity'));

                    const data = {
                        name: formData.get('itemName'),
                        category: formData.get('category'),
                        totalQuantity: newTotal,
                        serialNumber: formData.get('serialNumber'),
                        status: formData.get('status'),
                        allowedCourses: allowed,
                        photo: photoPath
                    };

                    if (!await InventoryService.updateItem(id, data) || !form.isConnected) return;
                    Utils.showToast('Cambios guardados correctamente');
                    ModalManager.close();
                    InventoryList.handleFilter(InventoryList.currentCategoryFilter); // Refresh
                    } finally { InventoryList._savingItem = false; }
                }
            }
        ]);
    },

    async deleteItem(id) {
        if (confirm('¿Seguro que quieres eliminar este elemento?')) {
            if (!await InventoryService.deleteItem(id)) return;
            this.handleFilter(this.currentCategoryFilter); // Refresh with current filter
        }
    },

    showImage(url, title) {
        if (!url) return;
        const content = `
            <div class="wh-image-preview-box">
                <img src="${Utils.sanitizeHTML(url)}">
            </div>
        `;
        ModalManager.show(title || 'Vista Previa', content, [
            {
                text: 'Cerrar',
                class: 'btn-secondary',
                action: () => ModalManager.close()
            }
        ]);
    }
};

window.InventoryList = InventoryList;
